import * as React from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '../../../lib/utils'
import { useDebugMode } from '../../../lib/debug-mode'
import { JobLogs } from '../../../components/JobLogs/JobLogs'

interface LogSectionProps {
  jobId: string
  logCount: number
}

/**
 * Full-width collapsible "Activity log" at the bottom of the agent-ingest page.
 * Collapsed by default — the spine + understanding column carry the run; this is
 * the tool-I/O debug log for when you don't trust the summary. Wraps `JobLogs`
 * unchanged; the header surfaces the event count.
 */
export function LogSection({ jobId, logCount }: LogSectionProps) {
  const debugMode = useDebugMode()
  const [open, setOpen] = React.useState(false)

  if (!debugMode) return null

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center gap-2 px-4 py-2.5 text-left text-sm font-medium tracking-tight text-foreground transition-colors hover:bg-accent/40"
      >
        <ChevronRight
          aria-hidden
          className={cn(
            'size-4 shrink-0 text-muted-foreground transition-transform duration-200 ease-out',
            open && 'rotate-90'
          )}
        />
        <span className="flex-1 text-11 font-medium tracking-[0.08em] text-muted-foreground uppercase">
          Activity log
        </span>
        <span className="font-mono text-xs text-muted-foreground tabular-nums">
          {logCount} {logCount === 1 ? 'event' : 'events'}
        </span>
      </button>
      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-out',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="overflow-hidden">
          <div className="border-t border-border px-4 py-3">
            <JobLogs jobId={jobId} emptyHint="Waiting for the agent to start…" />
          </div>
        </div>
      </div>
    </div>
  )
}

export default LogSection
